import { useCallback, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { DraftsButton } from '../components/DraftsButton';
import { NewProjectButton } from '../components/NewProjectButton';
import { ProjectFilterBar } from '../components/ProjectFilterBar';
import { ProjectTable } from '../components/ProjectTable';
import { useProjectFilterOptions, useProjects } from '../hooks/useProjects';
import { countNeedingAction } from '../lib/flags';
import { groupProjectsByMonth, splitPastMonths } from '../lib/months';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { EmptyState } from '@/shared/components/EmptyState';
import { PROJECT_STATUSES, isProjectStatus } from '@/shared/constants/statuses';
import { messages } from '@/shared/constants/messages';
import { toUserMessage } from '@/shared/lib/errors';
import { pluralize } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';
import type { ProjectFilters, ProjectWithContext } from '@/shared/types/domain';

/**
 * "O que está acontecendo com os projetos que eu acompanho?"
 *
 * Uma tabela só, do mês corrente em diante, agrupada por mês. As colunas são
 * `Data · Situação · Título · Ministério · Responsável · Tempo de espera`, e o
 * porquê da forma — tabela em vez de cartões, e por que continua não sendo um
 * kanban — está em `components/ProjectTable.tsx`.
 *
 * **O eixo da página é a DATA, e não a situação.** A situação é uma coluna;
 * gastar TAMBÉM a estrutura da tela com ela diria a mesma coisa duas vezes e
 * deixaria "quando isso acontece?" sem resposta — e planejar e cobrar exigem as
 * duas perguntas respondidas ao mesmo tempo.
 *
 * **Uma tabela contínua, e não duas seções.** Houve uma divisão entre "projetos
 * ativos" e "projetos arquivados", e ela saiu: a coluna de situação já diz se o
 * projeto acabou, e quem quer só o arquivo tem o filtro de situação logo acima.
 * Duas listas obrigavam a pessoa a entender por que um projeto aparecia numa e
 * não na outra antes de encontrar o que procurava.
 *
 * **Onde a tabela ABRE é decisão de produto.** Ela inclui o histórico inteiro,
 * e começar pelo projeto concluído mais antigo faria "rolar até hoje" ser o
 * primeiro gesto de toda visita. Os meses anteriores continuam na mesma ordem
 * crescente, na mesma tabela, atrás de um clique (`splitPastMonths`).
 *
 * **Por que continua diferente de `/analise`.** Os VERBOS diferem: aprovar,
 * solicitar ajustes e rejeitar são de lá (US-19), e nenhum entra aqui — esta
 * tela descreve estado, aquela muda estado. E a ORDENAÇÃO é oposta: a fila é
 * por data de submissão com dias de espera (US-18), enquanto aqui a ordem é a
 * do evento.
 *
 * Rascunho NÃO entra (`excludeDrafts`): é trabalho pela metade de uma pessoa
 * só, e tem tela própria em `/rascunhos` (US-16).
 *
 * Os filtros moram na URL, e não em `useState`: assim o botão voltar do
 * navegador desfaz um filtro em vez de sair da tela, e um recorte pode ser
 * colado numa conversa. Quem recebe o link só vê o que a RLS dele permitir.
 */

/** Sem `draft`: filtro que só pode devolver vazio não é oferecido. */
const ARCHIVE_STATUSES = PROJECT_STATUSES.filter((status) => status !== 'draft');

export function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * Estado local, e não URL, ao contrário dos filtros logo abaixo — e a
   * diferença é de natureza. Um filtro é um RECORTE: vale a pena colar numa
   * conversa e o botão voltar deve desfazê-lo. "Estou olhando o histórico" é
   * uma posição de leitura de um instante; empilhá-la no histórico do navegador
   * transformaria o botão voltar num desfazedor de rolagem.
   *
   * `null` significa "ainda não decidi" — e é o que permite o padrão depender
   * do RESULTADO. Filtrar por "Concluído" devolve, quase sempre, só projetos de
   * meses anteriores; com o histórico fechado por regra, a tela responderia
   * "nenhum projeto com esse recorte" ao lado de um botão dizendo que existem
   * cinco meses anteriores. A promessa é "abre no mês corrente", não "esconde o
   * passado": quando não há nada do mês corrente em diante, abrir o histórico É
   * abrir no que existe.
   */
  const [expanded, setExpanded] = useState<boolean | null>(null);

  const filters = useMemo<ProjectFilters>(() => {
    const status = searchParams.get('situacao');
    return {
      coordinationId: searchParams.get('coordenacao') ?? undefined,
      ministryId: searchParams.get('ministerio') ?? undefined,
      // `draft` na URL escrita à mão cai em "todas as situações": a tela não
      // tem rascunho para mostrar, e um recorte vazio sem explicação parece
      // sistema quebrado.
      status: status && isProjectStatus(status) && status !== 'draft' ? status : undefined,
      search: searchParams.get('busca') ?? undefined,
      excludeDrafts: true,
    };
  }, [searchParams]);

  const handleChange = useCallback(
    (next: ProjectFilters) => {
      const params = new URLSearchParams();
      if (next.coordinationId) params.set('coordenacao', next.coordinationId);
      if (next.ministryId) params.set('ministerio', next.ministryId);
      if (next.status) params.set('situacao', next.status);
      if (next.search?.trim()) params.set('busca', next.search.trim());
      // `replace`: digitar num campo de busca não deve empilhar uma entrada de
      // histórico por letra.
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const { data: options } = useProjectFilterOptions();
  const { data: projects, isPending, error } = useProjects(filters);

  const hasFilters = Boolean(
    filters.coordinationId || filters.ministryId || filters.status || filters.search,
  );

  // `projects` como dependência, e não o relógio: reagrupar a cada minuto
  // moveria uma linha de mês embaixo do cursor de quem está lendo. O corte é
  // resolvido na carga, e a próxima invalidação de cache o atualiza.
  const { past, current } = useMemo(
    () => splitPastMonths(groupProjectsByMonth(projects ?? [])),
    [projects],
  );

  const showPast = expanded ?? current.length === 0;

  const visible = useMemo(
    () => (showPast ? [...past, ...current] : current).flatMap((group) => group.items),
    [showPast, past, current],
  );

  const emptyTitle = hasFilters ? 'Nenhum projeto com esse recorte' : messages.empty.projects;
  const emptyDescription = hasFilters
    ? 'Tente remover um dos filtros acima.'
    : 'Assim que um ministério registrar um projeto, ele aparece aqui.';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projetos"
        description={describe(projects, options?.ministries.length)}
        actions={
          <>
            {/* Mesmo par da barra do Calendário, na mesma ordem — as duas
                telas que criam projeto oferecem a mesma volta para o que ficou
                pela metade. */}
            <DraftsButton />
            <NewProjectButton />
          </>
        }
      />

      <ProjectFilterBar
        filters={filters}
        onChange={handleChange}
        options={options}
        statuses={ARCHIVE_STATUSES}
      />

      {isPending ? <LoadingState label="Carregando projetos…" /> : null}

      {error ? (
        <EmptyState title="Não foi possível carregar" description={toUserMessage(error)} />
      ) : null}

      {projects ? (
        <div>
          {/* O botão fica ACIMA da tabela porque é onde o mês que ele revela
              vai aparecer — um controle longe do que ele muda obriga a
              procurar o efeito. Só existe quando há histórico: nas primeiras
              semanas do sistema não haveria mês anterior nenhum. */}
          {past.length > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded(!showPast)}
              aria-expanded={showPast}
              className="mb-1 flex items-center gap-1.5 rounded-md py-1.5 text-sm font-medium text-muted-foreground transition-colors [transition-duration:110ms] hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  'size-4 transition-transform duration-base ease-out',
                  showPast ? 'rotate-180' : 'rotate-0',
                )}
              />
              {showPast
                ? 'Ocultar meses anteriores'
                : `Ver ${pluralize(past.length, 'mês anterior', 'meses anteriores')}`}
            </button>
          ) : null}

          <ProjectTable
            projects={visible}
            groupByMonth
            emptyTitle={emptyTitle}
            emptyDescription={emptyDescription}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * A linha do cabeçalho carrega a cobrança.
 *
 * O contador âmbar morava no cabeçalho de uma seção colapsável; sem seções, ele
 * subiu para cá — e ganhou com a mudança, porque agora não depende de a pessoa
 * ter rolado até o mês certo para ser visto. Sem nada pendente ele não aparece:
 * um "0 precisam de ação" fixo treina o olho a ignorar o lugar onde o número de
 * verdade vai aparecer um dia.
 */
function describe(
  projects: readonly ProjectWithContext[] | undefined,
  ministryCount: number | undefined,
): string | undefined {
  if (!projects) {
    return ministryCount === undefined
      ? undefined
      : `Você acompanha ${pluralize(ministryCount, 'ministério', 'ministérios')}.`;
  }

  const total = pluralize(projects.length, 'projeto', 'projetos');
  const pending = countNeedingAction(projects);

  return pending > 0 ? `${total} · ${pending} precisam de ação` : total;
}
